import { useState, useRef, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Text,
  IconButton,
  Avatar,
  Button,
  Input,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { IoSend, IoChatbubblesOutline, IoArrowBack } from "react-icons/io5";
import { AuthContext } from "../store/auth-context";
import { useMessages } from "../store/messages-context";
import { resolveDisplayName, resolveProfileImageUrl } from "../util/profileImage";
import { getUserPresence, isSupabaseBackend } from "../services/backend";

const SIDEBAR_W = "320px";
const NEAR_BOTTOM_THRESHOLD_PX = 80;
const PRESENCE_POLL_MS = 20_000;

function Messages() {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const {
    conversations,
    activeConversationId,
    setActiveConversationId,
    sendMessage,
    ensureConversationLoaded,
  } = useMessages();
  const [inputValue, setInputValue] = useState("");
  const [isTyping] = useState(false);
  const [peerOnline, setPeerOnline] = useState(null);
  const { conversationId } = useParams();
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const navigate = useNavigate();

  const nearBottomRef = useRef(true);
  const lastMessageSigRef = useRef({ convId: "", count: 0, lastKey: "" });

  const isLgUp = useBreakpointValue({ base: false, lg: true });
  const isCompact = !isLgUp;

  const pageBg = useColorModeValue("#f8fafc", "surface.900");
  const divider = useColorModeValue("blackAlpha.100", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.500", "whiteAlpha.600");
  const subtle = useColorModeValue("gray.400", "whiteAlpha.500");
  const inboxHover = useColorModeValue("gray.50", "surface.800");
  const inboxActive = useColorModeValue("brand.50", "whiteAlpha.50");
  const chatBg = useColorModeValue("#f1f5f9", "surface.900");
  const bubbleThem = useColorModeValue("white", "surface.600");
  const bubbleThemText = useColorModeValue("gray.800", "whiteAlpha.900");
  const bubbleMe = useColorModeValue("brand.600", "brand.700");
  const bubbleMeText = "white";
  const inputBg = useColorModeValue("white", "surface.700");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const headerBg = useColorModeValue("white", "surface.900");
  const activeColor = useColorModeValue("brand.600", "brand.500");
  const iconHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const emptyStateIconBg = useColorModeValue("brand.50", "whiteAlpha.50");
  const bubbleThemShadow = useColorModeValue("sm", "none");
  const offlineColor = useColorModeValue("gray.500", "whiteAlpha.500");

  const compactMinH = "calc(100dvh - 54px - 72px - env(safe-area-inset-bottom, 0px))";
  const chatMinH = "calc(100dvh - 72px - env(safe-area-inset-bottom, 0px))";

  const activeConversation = conversations.find((c) => c.conversationId === activeConversationId);
  const peerUserId =
    activeConversation?.userId ?? activeConversation?.otherUserId ?? null;

  useEffect(() => {
    if (!peerUserId || !isSupabaseBackend()) {
      setPeerOnline(null);
      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const tick = async () => {
      try {
        const presence = await getUserPresence(peerUserId);
        if (!cancelled) setPeerOnline(Boolean(presence?.online));
      } catch {
        if (!cancelled) setPeerOnline(false);
      }
      if (!cancelled) {
        timerId = window.setTimeout(tick, PRESENCE_POLL_MS);
      }
    };

    setPeerOnline(null);
    tick();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [peerUserId]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [activeConversationId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = dist < NEAR_BOTTOM_THRESHOLD_PX;
  };

  useEffect(() => {
    const messages = activeConversation?.messages;
    if (!messages || messages.length === 0) {
      lastMessageSigRef.current = { convId: activeConversationId, count: 0, lastKey: "" };
      return;
    }
    const lastMessage = messages[messages.length - 1];
    const lastKey =
      lastMessage?.messageId ||
      lastMessage?.timestamp ||
      `${lastMessage?.senderId || ""}:${lastMessage?.content || ""}`;
    const sig = lastMessageSigRef.current;
    const isConvChange = sig.convId !== activeConversationId;
    const hasNewMessage =
      isConvChange || messages.length > sig.count || sig.lastKey !== lastKey;

    if (!hasNewMessage) return;

    const isMine = lastMessage?.senderId === authCtx.authenticatedUser?.id;
    if (isConvChange || isMine || nearBottomRef.current) {
      scrollToBottom();
    }

    lastMessageSigRef.current = {
      convId: activeConversationId,
      count: messages.length,
      lastKey,
    };
  }, [activeConversation?.messages, activeConversationId, authCtx.authenticatedUser?.id]);

  useEffect(() => {
    if (!conversationId || !authCtx.authenticatedUser?.id) return undefined;
    if (conversations.some((c) => c.conversationId === conversationId)) return undefined;
    let cancelled = false;
    (async () => {
      const built = await ensureConversationLoaded?.(conversationId);
      if (!cancelled && built) {
        setActiveConversationId(conversationId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    conversations,
    authCtx.authenticatedUser?.id,
    ensureConversationLoaded,
    setActiveConversationId,
  ]);

  useEffect(() => {
    if (conversations.length === 0) {
      if (activeConversationId && !conversationId) setActiveConversationId("");
      return;
    }
    if (conversationId && conversations.some((c) => c.conversationId === conversationId)) {
      if (activeConversationId !== conversationId) setActiveConversationId(conversationId);
      return;
    }
    if (
      activeConversationId &&
      !conversations.some((c) => c.conversationId === activeConversationId)
    ) {
      setActiveConversationId("");
    }
  }, [conversations, conversationId, activeConversationId, setActiveConversationId]);

  useEffect(() => {
    if (!conversationId) {
      setActiveConversationId("");
    }
    return () => {
      setActiveConversationId("");
    };
  }, [conversationId, setActiveConversationId]);

  const handleSendMessage = async () => {
    const sentContent = inputValue.trim();
    if (!sentContent || !activeConversation) return;

    const convId = activeConversation.conversationId;
    setInputValue("");
    nearBottomRef.current = true;

    try {
      await sendMessage(convId, sentContent);
    } catch (error) {
      console.error("Failed to send message:", error);
      setInputValue(sentContent);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const conversationPerson = (c) => c?.user ?? c;

  const displayName = (c) =>
    resolveDisplayName(conversationPerson(c), t("common.unknown"));

  const conversationPhoto = (c) => {
    const url = resolveProfileImageUrl(conversationPerson(c));
    return url && !url.includes("ui-avatars.com") ? url : undefined;
  };

  const formatInboxTime = (date) => {
    if (!date) return "";
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return "";

    const now = new Date();
    const isToday =
      dateObj.getDate() === now.getDate() &&
      dateObj.getMonth() === now.getMonth() &&
      dateObj.getFullYear() === now.getFullYear();

    if (isToday) {
      return dateObj.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    return dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const hasConversations = conversations.length > 0;
  const showEmptyInbox = !hasConversations;
  const showInboxPanel = isCompact ? hasConversations && !activeConversation : true;
  const showChatPanel = isCompact ? (showEmptyInbox || !!activeConversation) : true;
  const showSelectPrompt = !isCompact && hasConversations && !activeConversation;
  const inConversationView = isCompact && !!activeConversation;

  const openConversation = (convId) => {
    setActiveConversationId(convId);
    if (isCompact) {
      navigate(`/messages/${convId}`);
    }
  };

  const backToInbox = () => {
    setActiveConversationId("");
    navigate("/Messages");
  };

  const inboxList = (
    <Box flex={1} overflowY="auto">
      {conversations.map((conversation, index) => {
        const active = activeConversationId === conversation.conversationId;
        return (
          <Flex
            key={conversation.conversationId}
            px={4}
            py={3.5}
            cursor="pointer"
            bg={active ? inboxActive : "transparent"}
            borderBottom={index < conversations.length - 1 ? "1px solid" : "0"}
            borderColor={divider}
            transition="background 0.15s"
            _hover={{ bg: active ? inboxActive : inboxHover }}
            onClick={() => openConversation(conversation.conversationId)}
            align="flex-start"
            gap={3}
          >
            <Avatar
              size="md"
              flexShrink={0}
              src={conversationPhoto(conversation)}
              name={displayName(conversation)}
              bg="brand.700"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/profile/${conversation.userId}`);
              }}
            />
            <Flex direction="column" flex={1} minW={0} gap={1}>
              <Flex align="center" justify="space-between" gap={2}>
                <Text fontWeight="semibold" fontSize="sm" color={titleColor} noOfLines={1}>
                  {displayName(conversation)}
                </Text>
                <Text fontSize="xs" color={subtle} flexShrink={0}>
                  {formatInboxTime(conversation.lastMessageTime)}
                </Text>
              </Flex>
              <Text fontSize="sm" color={muted} noOfLines={1} lineHeight="short">
                {conversation.lastMessage && conversation.lastMessage.length > 56
                  ? `${conversation.lastMessage.slice(0, 56)}…`
                  : conversation.lastMessage || t("common.emDash")}
              </Text>
            </Flex>
          </Flex>
        );
      })}
    </Box>
  );

  const emptyInbox = (
    <Flex
      flex={1}
      direction="column"
      align="center"
      justify="center"
      px={6}
      py={10}
      gap={5}
      textAlign="center"
      bg={pageBg}
    >
      <Flex
        align="center"
        justify="center"
        w="80px"
        h="80px"
        borderRadius="full"
        bg={emptyStateIconBg}
      >
        <Box as={IoChatbubblesOutline} color="brand.500" w="36px" h="36px" />
      </Flex>
      <Box>
        <Text fontSize="lg" fontWeight="semibold" color={titleColor} mb={2}>
          {t("messages.noChatsTitle")}
        </Text>
        <Text fontSize="sm" color={muted} maxW="280px" mx="auto" lineHeight="tall">
          {t("messages.noChatsBody")}
        </Text>
      </Box>
      <Button
        onClick={() => navigate("/likes")}
        size="md"
        h="44px"
        px={6}
        fontWeight="semibold"
        borderRadius="full"
        bg="brand.600"
        color="white"
        _hover={{ bg: "brand.500" }}
        _active={{ transform: "scale(0.98)" }}
      >
        {t("messages.seeWhoLiked")}
      </Button>
    </Flex>
  );

  const chatHeader = activeConversation && (
    <Flex
      px={3}
      py={3}
      borderBottom="1px solid"
      borderColor={divider}
      bg={headerBg}
      align="center"
      gap={3}
      flexShrink={0}
      sx={{ paddingTop: inConversationView ? "calc(12px + env(safe-area-inset-top, 0px))" : undefined }}
    >
      {isCompact && (
        <IconButton
          aria-label={t("common.back")}
          icon={<IoArrowBack size={20} />}
          variant="ghost"
          size="sm"
          minW="36px"
          h="36px"
          color={titleColor}
          onClick={backToInbox}
          _hover={{ bg: iconHover }}
        />
      )}
      <Avatar
        size="sm"
        src={conversationPhoto(activeConversation)}
        name={displayName(activeConversation)}
        bg="brand.700"
        cursor="pointer"
        onClick={() => navigate(`/profile/${activeConversation.userId}`)}
      />
      <Box flex={1} minW={0}>
        <Text fontWeight="semibold" color={titleColor} fontSize="sm" noOfLines={1}>
          {displayName(activeConversation)}
        </Text>
        <Text fontSize="xs" color={peerOnline ? activeColor : offlineColor} mt={0.5}>
          {peerOnline == null
            ? t("common.emDash", { defaultValue: "—" })
            : peerOnline
              ? t("messages.online")
              : t("messages.offline")}
        </Text>
      </Box>
    </Flex>
  );

  const messageList = (
    <Box
      ref={messagesAreaRef}
      onScroll={handleMessagesScroll}
      flex={1}
      overflowY="auto"
      px={4}
      py={4}
      bg={chatBg}
    >
      {activeConversation?.messages && activeConversation.messages.length > 0 ? (
        activeConversation.messages.map((message, idx) => {
          const isMe = message.senderId === authCtx.authenticatedUser.id;
          const key = message.messageId ?? `msg-${idx}-${message.timestamp || idx}`;
          return (
            <Flex
              key={key}
              justify={isMe ? "flex-end" : "flex-start"}
              align="flex-end"
              gap={2}
              mb={3}
            >
              {!isMe && (
                <Avatar
                  size="sm"
                  src={conversationPhoto(activeConversation)}
                  name={displayName(activeConversation)}
                  bg="brand.700"
                />
              )}
              <Box
                maxW="78%"
                px={4}
                py={2.5}
                borderRadius="2xl"
                bg={isMe ? bubbleMe : bubbleThem}
                color={isMe ? bubbleMeText : bubbleThemText}
                boxShadow={isMe ? "none" : bubbleThemShadow}
              >
                <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word" lineHeight="tall">
                  {message.content}
                </Text>
              </Box>
              {isMe && (
                <Avatar
                  size="sm"
                  src={resolveProfileImageUrl(authCtx.authenticatedUser) || undefined}
                  name={authCtx.authenticatedUser?.firstName || t("common.you")}
                  bg="brand.700"
                />
              )}
            </Flex>
          );
        })
      ) : (
        <Flex align="center" justify="center" minH="200px" color={subtle} fontSize="sm">
          {t("messages.noMessages")}
        </Flex>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );

  const inputBar = (
    <Box
      px={4}
      py={3}
      bg={headerBg}
      borderTop="1px solid"
      borderColor={divider}
      flexShrink={0}
      pb="calc(12px + env(safe-area-inset-bottom, 0px))"
    >
      <Flex align="center" gap={2}>
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={t("messages.messagePlaceholder")}
          disabled={isTyping}
          h="44px"
          bg={inputBg}
          color={titleColor}
          borderColor={inputBorder}
          borderRadius="full"
          fontSize="sm"
          flex={1}
          _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
          _placeholder={{ color: subtle }}
        />
        <IconButton
          aria-label={t("messages.sendMessage")}
          icon={<IoSend />}
          onClick={handleSendMessage}
          isDisabled={!inputValue.trim() || isTyping}
          borderRadius="full"
          size="md"
          minW="44px"
          h="44px"
          bg="brand.600"
          color="white"
          _hover={{ bg: "brand.500" }}
          _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
        />
      </Flex>
    </Box>
  );

  return (
    <Flex
      className="payinapo-messages"
      position="relative"
      w="100%"
      mx={isCompact ? -3 : { base: -3, sm: -4, md: -6, lg: 0 }}
      mt={isCompact ? (inConversationView ? -3 : -3) : { base: -3, md: -8, lg: 0 }}
      mb={isCompact ? -3 : { base: -3, md: -8, lg: 0 }}
      minH={
        inConversationView
          ? chatMinH
          : isCompact
            ? compactMinH
            : { base: compactMinH, lg: "calc(100vh - 120px)" }
      }
      maxH={
        inConversationView
          ? chatMinH
          : isCompact
            ? compactMinH
            : { base: compactMinH, lg: "calc(100vh - 100px)" }
      }
      bg={pageBg}
      borderRadius={isCompact ? 0 : { base: 0, lg: "xl" }}
      overflow="hidden"
    >
      {showInboxPanel && (
        <Box
          as="aside"
          w={isCompact ? "100%" : SIDEBAR_W}
          flexShrink={0}
          h="100%"
          minH="inherit"
          bg={pageBg}
          borderRight={isCompact ? "0" : "1px solid"}
          borderColor={divider}
          display="flex"
          flexDirection="column"
        >
          {!isCompact && (
            <Box px={4} py={4} borderBottom="1px solid" borderColor={divider}>
              <Text fontSize="lg" fontWeight="bold" color={titleColor}>
                {t("messages.messagesTitle")}
              </Text>
            </Box>
          )}
          {inboxList}
        </Box>
      )}

      {showChatPanel && (
        <Flex direction="column" flex={1} minW={0} minH="inherit" bg={chatBg}>
          {showEmptyInbox ? (
            emptyInbox
          ) : showSelectPrompt ? (
            <Flex flex={1} align="center" justify="center" px={6} textAlign="center" bg={chatBg}>
              <Box>
                <Text fontSize="md" fontWeight="medium" color={muted} mb={1}>
                  {t("messages.selectConversationTitle")}
                </Text>
                <Text fontSize="sm" color={subtle}>
                  {t("messages.selectConversationBody")}
                </Text>
              </Box>
            </Flex>
          ) : (
            <>
              {chatHeader}
              {messageList}
              {inputBar}
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export default Messages;
